/**
 * Settings API — Layer 1 of the three-layer pattern.
 *
 * Pure async functions, no TanStack, no React imports.
 */
import { apiClient } from "@/shared/lib/axios";
import { AUTH } from "@/shared/lib/apiPaths";
import type { MeResponse, NumberFormat, UserPreferences } from "@/shared/types/user";

export type { NumberFormat, UserPreferences };

/**
 * Matches backend PATCH /auth/preferences/ accepted fields.
 * Backend accepts camelCase: webPush, emailPush, numberFormat.
 */
export interface UpdatePreferencesInput {
  language?: string;
  theme?: string;
  numberFormat?: NumberFormat;
  webPush?: boolean;
  emailPush?: boolean;
}

/** Fetches user preferences from /me/, falling back to the legacy preferences endpoint. */
export async function getUserPreferences(): Promise<UserPreferences> {
  const { data: me } = await apiClient.get<MeResponse>(`${AUTH}/me`);
  if (me.preferences) return me.preferences;

  const { data } = await apiClient.get<UserPreferences>(`${AUTH}/preferences`);
  return data;
}

/** Updates user preferences through the preferences endpoint. */
export async function updateUserPreferences(
  input: UpdatePreferencesInput
): Promise<UserPreferences> {
  const { data } = await apiClient.patch<UserPreferences>(
    `${AUTH}/preferences`,
    input
  );
  return data;
}